import React, { useState, useEffect, useRef } from "react";
import { Button } from "../../components/Button.tsx";
import { Badge } from "../../components/Badge.tsx";
import Layout from "../../components/Layout.tsx";
import { RiAddLine, RiDownloadLine, RiArrowLeftSLine, RiExternalLinkLine, RiCloseLine, RiBuilding2Line, RiChat3Line, RiTimeLine, RiInboxLine } from "@remixicon/react";
import {
  EllipsisIcon,
  ClipboardListIcon,
  UsersIcon,
  FileTextIcon,
  BanknoteIcon,
  ClockIcon,
  SparklesIcon,
} from "./Icons.tsx";
import { toast } from "react-hot-toast";
import { DataSource, DataChatMessage, EndpointInfo } from "../../types.ts";
import { fetchDataStudioEndpoints, deleteGeneratedData, deleteDataSource, restoreSession, fetchSessions, deleteSession, fetchEasyflexAdministrations, fetchLibraryItems, fetchLibraryItemPreview, fetchDataStudioTask, cancelDataStudioTask, saveDataStudioTask, waitForDataStudioTask, UserLibraryItem } from "../../services/ApiService.ts";
import { useUser } from "../../context/UserContext.tsx";
import DataChat from "./DataChat.tsx";
import DataLoadingWizard from "./DataLoadingWizard.tsx";
import { useExportDownload } from "./useExportDownload.ts";
import { parseUtcDate, greeting } from "../../utils.ts";
import { formatModuleLabel, libraryItemSizeError, libraryItemToDataSource, summarizeParameters } from "./library.ts";


interface Conversation {
  id: string;
  name: string;
  dataSources: DataSource[];
  generatedData: Record<string, Record<string, unknown>[]>;
  messages?: DataChatMessage[];
  sessionId?: string;
  active: boolean;
  restoring?: boolean;
}

const MAX_LIBRARY_CHIPS = 5;

const isPending = (source: DataSource): boolean =>
  source.state === "QUEUED" || source.state === "RUNNING";

const hasFailed = (source: DataSource): boolean =>
  source.state === "FAILED" || source.state === "CANCELLED";

const renderPreviewCell = (value: unknown) => {
  if (typeof value !== "object" || value === null) {
    return String(value ?? "");
  }
  if (Array.isArray(value) && value.length === 0) {
    return <span className="text-gray-400">Geen items</span>;
  }
  return (
    <details>
      <summary className="cursor-pointer text-xs font-medium text-blue-700 select-none whitespace-nowrap">
        {Array.isArray(value) ? `${value.length} ${value.length === 1 ? "item" : "items"}` : "Details"}
      </summary>
      <pre className="mt-2 max-h-64 overflow-auto text-xs text-gray-600">{JSON.stringify(value, null, 2)}</pre>
    </details>
  );
};

const DataStudio = () => {
  const { user } = useUser();
  const runExport = useExportDownload();
  const [administrationCode, setAdministrationCode] = useState<number | null>(null);
  const [administrationNames, setAdministrationNames] = useState<Record<number, string>>({});
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [activeConversationId, setActiveConversationId] = useState<string | null>(null);
  const [isWizardOpen, setIsWizardOpen] = useState(false);
  const [showInitialView, setShowInitialView] = useState(true);
  const [viewDataModal, setViewDataModal] = useState<{ data: Record<string, unknown>[]; title: string; totalRows?: number | null } | null>(null);
  const [commonEndpointInfos, setCommonEndpointInfos] = useState<EndpointInfo[]>([]);
  const [preselectedEndpoint, setPreselectedEndpoint] = useState<string | undefined>(undefined);
  const [wizardMode, setWizardMode] = useState<"wizard" | "library">("wizard");
  const [deleteConfirm, setDeleteConfirm] = useState<
    | { type: "generated"; key: string }
    | { type: "datasource"; source: DataSource }
    | null
  >(null);
  const [adminStats, setAdminStats] = useState<Record<number, { chatCount: number; lastOpened: string | null }>>({});
  const [libraryItems, setLibraryItems] = useState<UserLibraryItem[]>([]);

  const formatLastOpened = (iso: string | null): string => {
    if (!iso) return "Nog niet gebruikt";
    return parseUtcDate(iso).toLocaleString("nl-NL", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handleSwitchOrganisation = () => {
    setAdministrationCode(null);
    setConversations([]);
    setActiveConversationId(null);
    setShowInitialView(true);
  };

  // Auto-select if only one org, otherwise show picker immediately and load names in background
  useEffect(() => {
    if (!user) return;
    if (user.administrationCodes.length === 1) {
      setAdministrationCode(user.administrationCodes[0]);
    } else {
      fetchEasyflexAdministrations()
        .then((admins) => {
          const names: Record<number, string> = {};
          for (const a of admins) {
            if (user.administrationCodes.includes(Number(a.id))) {
              names[Number(a.id)] = a.name;
            }
          }
          setAdministrationNames(names);
        })
        .catch(() => {}); // names are cosmetic — picker still renders without them
    }
  }, [user]);

  useEffect(() => {
    if (!user || administrationCode) return;
    user.administrationCodes.forEach((code) => {
      fetchSessions(code).then((res) => {
        if (res.error || !res.data) return;
        const lastOpened = res.data.reduce<string | null>((latest, s) => {
          if (!s.last_opened) return latest;
          return !latest || s.last_opened > latest ? s.last_opened : latest;
        }, null);
        setAdminStats((prev) => ({ ...prev, [code]: { chatCount: res.data!.length, lastOpened } }));
      });
    });
  }, [user, administrationCode]);

  useEffect(() => {
    if (!administrationCode) return;
    fetchSessions(administrationCode).then((res) => {
      if (res.error || !res.data) return;
      setConversations(res.data.map((s) => ({
        id: s.id,
        name: s.name,
        dataSources: [],
        generatedData: {},
        sessionId: s.id,
        active: false,
      })));
    });
    fetchLibraryItems(administrationCode).then((res) => {
      if (!res.error && res.data) setLibraryItems(res.data);
    });
  }, [administrationCode]);

  useEffect(() => {
    if (!administrationCode) return;
    const loadCommonEndpoints = async () => {
      const response = await fetchDataStudioEndpoints();
      if (response.error || !response.data) return;
      const allEndpoints = response.data;

      const FALLBACK_IDS = ["ds_bi_gewerkte_weken", "ds_bi_loonopgave", "ds_bi_declaratieregels"];

      const ordered = FALLBACK_IDS
        .map((id) => allEndpoints.find((e) => e.id === id))
        .filter(Boolean) as EndpointInfo[];
      setCommonEndpointInfos(ordered);
    };
    loadCommonEndpoints();
  }, [administrationCode]);

  const prevConversationIdRef = useRef<string | null>(null);

  // Remove empty conversations when switching away from them
  useEffect(() => {
    const prevId = prevConversationIdRef.current;
    prevConversationIdRef.current = activeConversationId;
    if (!prevId || prevId === activeConversationId) return;
    setConversations((prev) => {
      const leaving = prev.find((c) => c.id === prevId);
      if (!leaving) return prev;
      const isEmpty =
        leaving.dataSources.length === 0 &&
        (leaving.messages?.length ?? 0) === 0 &&
        !leaving.restoring;
      return isEmpty ? prev.filter((c) => c.id !== prevId) : prev;
    });
  }, [activeConversationId]);

  // Auto-restore from Azure when switching to a conversation that has a sessionId but no loaded data
  useEffect(() => {
    if (!activeConversationId) return;
    const conv = conversations.find((c) => c.id === activeConversationId);
    if (!conv?.sessionId) return;
    if (conv.dataSources.length > 0 || conv.restoring) return;

    setConversations((prev) =>
      prev.map((c) => (c.id === activeConversationId ? { ...c, restoring: true } : c))
    );

    restoreSession(conv.sessionId).then((response) => {
      if (response.error || !response.data) {
        toast.error("Kon sessie niet herstellen vanuit Azure");
        setConversations((prev) => prev.filter((c) => c.id !== activeConversationId));
        return;
      }
      const { data_sources, messages } = response.data!;
      if (data_sources.length === 0 && (messages?.length ?? 0) === 0) {
        setConversations((prev) => prev.filter((c) => c.id !== activeConversationId));
        return;
      }
      setConversations((prev) =>
        prev.map((c) =>
          c.id === activeConversationId
            ? {
                ...c,
                dataSources: response.data!.data_sources,
                generatedData: response.data!.generated_data,
                messages: response.data!.messages,
                restoring: false,
              }
            : c
        )
      );
      toast.success("Sessie hersteld");
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps -- listing this would re-run the effect on changes it should ignore
  }, [activeConversationId]);

  const getEndpointName = (endpointId: string): string => {
    if (!endpointId.startsWith("ds_")) return endpointId;
    const withoutDs = endpointId.substring(3);
    const prefixes = ["bi_", "fw_", "ef_", "p4f_", "rf_", "wm_"];
    for (const prefix of prefixes) {
      if (withoutDs.startsWith(prefix)) {
        return withoutDs.substring(prefix.length);
      }
    }
    return withoutDs;
  };

  const getEndpointIcon = (endpointId: string, size = "w-5 h-5"): React.ReactNode => {
    const className = `${size} text-[rgba(96,147,172,0.7)]`;
    const iconMap: Record<string, React.ReactNode> = {
      "gewerkte_weken": <ClockIcon className={className} />,
      "loonopgave": <BanknoteIcon className={className} />,
      "declaratieregels": <ClipboardListIcon className={className} />,
      "arbeidscontract": <FileTextIcon className={className} />,
      "medewerkerlijst": <UsersIcon className={className} />,
      "actieve_plaatsingen": <FileTextIcon className={className} />,
      "plaatsingen": <FileTextIcon className={className} />,
      "persoonsgegevens": <UsersIcon className={className} />,
    };
    return iconMap[getEndpointName(endpointId)] || <FileTextIcon className={className} />;
  };

  const patchDataSource = (match: (source: DataSource) => boolean, patch: Partial<DataSource>) =>
    setConversations((prev) => {
      // Returning the same reference lets React skip the render when a poll tick changes nothing.
      if (!prev.some((conv) => conv.dataSources.some(match))) return prev;
      return prev.map((conv) => ({
        ...conv,
        dataSources: conv.dataSources.map((s) => (match(s) ? { ...s, ...patch } : s)),
      }));
    });

  const pendingTaskIds = conversations
    .flatMap((conv) => conv.dataSources)
    .filter(isPending)
    .map((source) => source.task_id)
    .filter((taskId): taskId is string => !!taskId)
    .join(",");

  // ponytail: every completion restarts the polls for the still-pending tasks (one extra status
  // call each). Give each task its own controller if a basket ever holds many fetches at once.
  useEffect(() => {
    if (!pendingTaskIds) return;
    const controller = new AbortController();

    for (const taskId of pendingTaskIds.split(",")) {
      waitForDataStudioTask(
        taskId,
        (task) => patchDataSource((s) => s.task_id === taskId && s.state !== task.state, { state: task.state }),
        controller.signal,
      )
        .then((task) => {
          patchDataSource((s) => s.task_id === taskId, {
            state: task.state,
            row_count: task.row_count,
            error_message: task.error_message,
          });
          if (task.state === "COMPLETED") {
            toast.success(`${task.source_name ?? "Data"} is opgehaald`);
          } else if (task.state === "FAILED") {
            toast.error(task.error_message ?? "Fout bij het ophalen van data");
          }
        })
        .catch((error) => {
          if (error instanceof DOMException && error.name === "AbortError") return;
          patchDataSource((s) => s.task_id === taskId, {
            state: "FAILED",
            error_message: error instanceof Error ? error.message : "Fout bij het ophalen van data",
          });
        });
    }

    return () => controller.abort();
  }, [pendingTaskIds]);

  const handleSaveToLibrary = async (source: DataSource) => {
    if (!source.task_id || !administrationCode) {
      toast.error("Geen data om op te slaan");
      return;
    }

    const filename = source.source_name || getEndpointName(source.endpoint_name);
    const response = await saveDataStudioTask(source.task_id, filename);
    if (response.error || !response.data) {
      toast.error(response.error ?? "Kon databron niet opslaan");
      return;
    }

    patchDataSource((s) => s.task_id === source.task_id, { library_item_id: response.data!.id });
    fetchLibraryItems(administrationCode).then((res) => {
      if (!res.error && res.data) setLibraryItems(res.data);
    });
    toast.success("Databron bewaard in de bibliotheek");
  };

  const handleCancelDataSource = async (source: DataSource) => {
    if (!source.task_id) return;
    await cancelDataStudioTask(source.task_id);
    // Polling replaces the source object on every state change, so identity is not a stable match.
    setConversations((prev) =>
      prev.map((conv) => ({
        ...conv,
        dataSources: conv.dataSources.filter((s) => s.task_id !== source.task_id),
      }))
    );
    toast.success("Ophalen geannuleerd");
  };

  const addedToBasketMessage = (source: DataSource): string =>
    isPending(source) ? "Data wordt opgehaald — je kunt alvast verder" : "Data toegevoegd aan mandje";

  const handleDataLoaded = (source: DataSource) => {
    // If no active conversation, create one
    if (!activeConversationId || conversations.length === 0) {
      const sessionId = crypto.randomUUID();
      const newConversation: Conversation = {
        id: sessionId,
        sessionId,
        name: `${getEndpointName(source.endpoint_name)} • ${new Date().toLocaleDateString("nl-NL", { day: "2-digit", month: "2-digit" })} ${new Date().toLocaleTimeString("nl-NL", { hour: "2-digit", minute: "2-digit" })}`,
        dataSources: [source],
        generatedData: {},
        active: true,
      };
      setConversations((prev) => [...prev, newConversation]);
      setActiveConversationId(newConversation.id);
      setShowInitialView(false);
      toast.success(addedToBasketMessage(source));
      return;
    }

    // Add to active conversation
    setConversations((prev) =>
      prev.map((conv) => {
        if (conv.id === activeConversationId) {
          // Check if source already exists in conversation (compare by endpoint, parameters, and fields)
          const exists = conv.dataSources.some((s) => {
            if (s.endpoint_name !== source.endpoint_name) return false;

            const sParams = JSON.stringify(s.parameters || {});
            const sourceParams = JSON.stringify(source.parameters || {});
            if (sParams !== sourceParams) return false;

            const sFields = JSON.stringify([...s.selected_fields].sort());
            const sourceFields = JSON.stringify([...source.selected_fields].sort());
            if (sFields !== sourceFields) return false;

            return true;
          });

          if (exists) {
            toast.error("Deze data is al toegevoegd aan het mandje");
            return conv;
          }

          toast.success(addedToBasketMessage(source));
          const updatedName = conv.dataSources.length === 0
            ? `${getEndpointName(source.endpoint_name)} • ${new Date().toLocaleDateString("nl-NL", { day: "2-digit", month: "2-digit" })} ${new Date().toLocaleTimeString("nl-NL", { hour: "2-digit", minute: "2-digit" })}`
            : conv.name;
          return { ...conv, name: updatedName, dataSources: [...conv.dataSources, source] };
        }
        return conv;
      })
    );
  };

  const handleDownloadLibraryItem = async (source: DataSource) => {
    if (!administrationCode || !source.task_id && !source.library_item_id) {
      toast.error("Geen data om te downloaden");
      return;
    }

    const endpointName = getEndpointName(source.endpoint_name);
    await runExport({
      administration_code: administrationCode,
      source: source.task_id
        ? { type: "TASK", task_id: source.task_id }
        : { type: "LIBRARY", library_item_id: source.library_item_id! },
      filename: source.source_name || endpointName,
      format: "XLSX",
    });
  };

  const handleViewDataSource = async (source: DataSource, title: string) => {
    if (source.library_item_id && administrationCode) {
      const response = await fetchLibraryItemPreview(source.library_item_id, administrationCode);
      if (response.error || !response.data) {
        toast.error("Kon voorbeelddata niet laden");
        return;
      }
      setViewDataModal({
        data: response.data.data,
        title,
        totalRows: response.data.row_count,
      });
      return;
    }

    if (source.task_id) {
      const response = await fetchDataStudioTask(source.task_id);
      if (response.error || !response.data) {
        toast.error("Kon voorbeelddata niet laden");
        return;
      }
      setViewDataModal({
        data: response.data.preview,
        title,
        totalRows: response.data.row_count,
      });
      return;
    }

    toast.error("Kon voorbeelddata niet laden");
  };

  const handleDownloadGeneratedData = async (tableName: string) => {
    const sessionId = conversations.find((conversation) => conversation.id === activeConversationId)?.sessionId;
    if (!administrationCode || !sessionId) {
      toast.error("De dataset is nog niet beschikbaar om te downloaden");
      return;
    }

    await runExport({
      administration_code: administrationCode,
      source: { type: "GENERATED", session_id: sessionId, table_name: tableName },
      filename: tableName,
      format: "XLSX",
    });
  };

  const handleNewConversation = () => {
    const newConversation: Conversation = {
      id: crypto.randomUUID(),
      // The client generates the chat ID; the first stream request attaches its source references.
      name: `Nieuw gesprek • ${new Date().toLocaleDateString("nl-NL", { day: "2-digit", month: "2-digit" })} ${new Date().toLocaleTimeString("nl-NL", { hour: "2-digit", minute: "2-digit" })}`,
      dataSources: [],
      generatedData: {},
      active: false,
    };
    setConversations((prev) => prev.map((c) => ({ ...c, active: false })).concat(newConversation));
    setActiveConversationId(newConversation.id);
    setShowInitialView(false);
  };

  const handleDeleteConversation = async (id: string) => {
    const conv = conversations.find((c) => c.id === id);
    if (conv?.sessionId) {
      const res = await deleteSession(conv.sessionId);
      if (res.error) {
        toast.error("Kon gesprek niet verwijderen");
        return;
      }
    }
    const remaining = conversations.filter((c) => c.id !== id);
    setConversations(remaining);
    if (activeConversationId === id) {
      const next = remaining[0];
      if (next) {
        handleSelectConversation(next.id);
      } else {
        setActiveConversationId(null);
        setShowInitialView(true);
      }
    }
    toast.success("Gesprek verwijderd");
  };

  const handleSelectConversation = (id: string) => {
    setActiveConversationId(id);
    setConversations((prev) =>
      prev.map((c) => ({ ...c, active: c.id === id }))
    );
    setShowInitialView(false);
  };

  const activeConversation = conversations.find((c) => c.id === activeConversationId);
  const activeDataSources = activeConversation?.dataSources || [];
  const readyDataSources = activeDataSources.filter((source) => !isPending(source) && !hasFailed(source));
  const activeGeneratedData = activeConversation?.generatedData || {};
  const baseLayoutProps = {
    title: "DataStudio",
    showBackButton: false,
    showNextButton: false,
  } as const;

  if (!user) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
      </div>
    );
  }

  if (!administrationCode) {
    return (
      <Layout
        {...baseLayoutProps}
        description="Selecteer een organisatie om mee te werken."
      >
        <div className="mx-auto flex w-full max-w-3xl flex-col gap-3">
          {user.administrationCodes.map((code) => {
            const name = administrationNames[code] ?? `Organisatie ${code}`;
            const stats = adminStats[code];
            return (
              <button
                key={code}
                onClick={() => setAdministrationCode(code)}
                className="group overflow-hidden rounded-2xl border border-gray-200 bg-white text-left shadow-sm transition-all hover:border-blue-400 hover:bg-blue-50/30 hover:shadow-md"
              >
                <div className="flex items-center gap-4 px-5 py-4">
                  <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl border border-blue-100 bg-blue-50 transition-colors group-hover:bg-blue-100">
                    <RiBuilding2Line className="h-5 w-5 text-blue-500" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-gray-900">{name}</div>
                    <div className="mt-0.5 text-xs text-gray-400">Code: {code}</div>
                  </div>
                  <RiArrowLeftSLine className="h-4 w-4 flex-shrink-0 rotate-180 text-gray-300 transition-colors group-hover:text-blue-400" />
                </div>
                {stats !== undefined && (
                  <div className="flex items-center gap-5 border-t border-gray-100 bg-gray-50/50 px-5 py-2.5">
                    <div className="flex items-center gap-1.5 text-xs text-gray-500">
                      <RiChat3Line className="h-3.5 w-3.5 text-gray-400" />
                      <span>{stats.chatCount} {stats.chatCount === 1 ? "gesprek" : "gesprekken"}</span>
                    </div>
                    <div className="flex items-center gap-1.5 text-xs text-gray-500">
                      <RiTimeLine className="h-3.5 w-3.5 text-gray-400" />
                      <span>{formatLastOpened(stats.lastOpened)}</span>
                    </div>
                  </div>
                )}
                {stats === undefined && (
                  <div className="border-t border-gray-100 bg-gray-50/50 px-5 py-2.5">
                    <div className="h-3 w-32 animate-pulse rounded bg-gray-200" />
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </Layout>
    );
  }

  if (showInitialView) {
    return (
      <>
        <Layout
          {...baseLayoutProps}
          description="Bekijk en analyseer je data in een gesprek."
        >
          <div className="-mx-6 -mb-6 flex min-h-[72vh] flex-col overflow-hidden">
            {user.administrationCodes.length > 1 && (
              <div className="flex justify-end px-6 pb-2">
                <button
                  onClick={handleSwitchOrganisation}
                  className="flex items-center gap-1.5 rounded-full border border-gray-200 bg-white px-3 py-1.5 text-xs text-gray-500 transition-colors hover:border-gray-300 hover:text-gray-800"
                >
                  <RiBuilding2Line className="h-3.5 w-3.5" />
                  {administrationNames[administrationCode] ?? `Organisatie ${administrationCode}`}
                  <RiArrowLeftSLine className="h-3.5 w-3.5 rotate-180 opacity-60" />
                </button>
              </div>
            )}

            <div className="flex flex-1 flex-col items-center justify-center px-4 py-4 text-center md:px-8">
              <div className="mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-blue-100">
                <SparklesIcon className="h-10 w-10 text-white" />
              </div>
              <h2 className="mb-2 text-xl font-semibold text-gray-900">
                {greeting()}
              </h2>
              <p className="mb-7 text-base text-gray-600">Wat wil je vandaag analyseren?</p>

              <div className="flex w-full max-w-5xl flex-wrap justify-center gap-3 px-0 md:px-8 xl:px-16">
                {/* Saved datasets are one click away; only when the library is
                    still empty do we fall back to suggested topics. */}
                {libraryItems.length > 0
                  ? libraryItems.slice(0, MAX_LIBRARY_CHIPS).map((item) => {
                      const meta = [
                        summarizeParameters(item.parameters),
                        item.row_count != null ? `${item.row_count.toLocaleString()} rijen` : "",
                      ]
                        .filter(Boolean)
                        .join(" · ");
                      return (
                        <button
                          key={item.id}
                          onClick={() => {
                            const sizeError = libraryItemSizeError(item);
                            if (sizeError) {
                              toast.error(sizeError, { duration: 10000 });
                              return;
                            }
                            handleDataLoaded(libraryItemToDataSource(item));
                          }}
                          className="inline-flex items-center gap-2 whitespace-nowrap rounded-full border border-gray-200 bg-white px-4 py-2 shadow-sm transition-all hover:border-blue-400 hover:bg-blue-50/50 hover:shadow-md"
                        >
                          {getEndpointIcon(item.module, "h-4 w-4")}
                          <span className="flex flex-col items-start leading-tight">
                            <span className="text-xs font-medium text-gray-700">{formatModuleLabel(item.module)}</span>
                            {meta && <span className="text-[10px] text-gray-500">{meta}</span>}
                          </span>
                        </button>
                      );
                    })
                  : commonEndpointInfos.map((endpoint) => (
                      <button
                        key={endpoint.id}
                        onClick={() => { setPreselectedEndpoint(endpoint.id); setWizardMode("wizard"); setIsWizardOpen(true); }}
                        className="inline-flex items-center gap-2 whitespace-nowrap rounded-full border border-gray-200 bg-white px-4 py-2.5 shadow-sm transition-all hover:border-blue-400 hover:bg-blue-50/50 hover:shadow-md"
                      >
                        {getEndpointIcon(endpoint.id, "h-4 w-4")}
                        <span className="text-xs font-medium text-gray-700">{formatModuleLabel(endpoint.id)}</span>
                      </button>
                    ))}
                <button
                  onClick={() => { setPreselectedEndpoint(undefined); setWizardMode("wizard"); setIsWizardOpen(true); }}
                  className="inline-flex items-center gap-2 whitespace-nowrap rounded-full border border-gray-200 bg-white px-4 py-2.5 shadow-sm transition-all hover:border-blue-400 hover:bg-blue-50/50 hover:shadow-md"
                >
                  <EllipsisIcon className="h-4 w-4 text-[rgba(96,147,172,0.7)]" />
                  <span className="text-xs font-medium text-gray-700">Nieuwe data laden</span>
                </button>
              </div>

              {libraryItems.length > MAX_LIBRARY_CHIPS && (
                <button
                  onClick={() => { setPreselectedEndpoint(undefined); setWizardMode("library"); setIsWizardOpen(true); }}
                  className="mt-3 text-xs text-blue-600 transition-colors hover:text-blue-700"
                >
                  Alle opgeslagen data ({libraryItems.length})
                </button>
              )}

              {conversations.length > 0 && (
                <div className="mt-8 flex w-full max-w-5xl flex-col items-center">
                  <h3 className="mb-3 text-xs font-semibold text-gray-700">Recente gesprekken:</h3>
                  <div className="flex flex-wrap justify-center gap-2">
                    {conversations.map((conv) => (
                      <button
                        key={conv.id}
                        onClick={() => handleSelectConversation(conv.id)}
                        className="inline-flex items-center whitespace-nowrap rounded-full border border-blue-200 px-4 py-2 text-xs text-blue-600 transition-colors hover:border-blue-300 hover:bg-blue-50/50 hover:text-blue-700"
                      >
                        {conv.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </Layout>

        <DataLoadingWizard
          isOpen={isWizardOpen}
          onClose={() => { setIsWizardOpen(false); setPreselectedEndpoint(undefined); }}
          onDataLoaded={handleDataLoaded}
          initialEndpoint={preselectedEndpoint}
          initialMode={wizardMode}
          libraryItems={libraryItems}
          onLibraryItemDeleted={(id) => setLibraryItems((prev) => prev.filter((b) => b.id !== id))}
          administrationCode={administrationCode ?? undefined}
        />
      </>
    );
  }

  return (
    <>
      <Layout
        {...baseLayoutProps}
        description="Bekijk en analyseer je data in een gesprek."
      >
        <div className="-m-6 flex h-[72vh] min-h-[72vh] flex-col overflow-hidden rounded-[1rem] border border-gray-200 bg-white">
          {user.administrationCodes.length > 1 && (
            <div className="flex justify-end px-6 pb-2">
              <button
                onClick={handleSwitchOrganisation}
                className="flex items-center gap-1.5 rounded-full border border-gray-200 bg-white px-3 py-1.5 text-xs text-gray-500 transition-colors hover:border-gray-300 hover:text-gray-800"
              >
                <RiBuilding2Line className="h-3.5 w-3.5" />
                {administrationNames[administrationCode] ?? `Organisatie ${administrationCode}`}
                <RiArrowLeftSLine className="h-3.5 w-3.5 rotate-180 opacity-60" />
              </button>
            </div>
          )}

          <div className="flex min-h-0 flex-1 flex-col lg:flex-row">
            {/* Left Sidebar */}
            <div className="flex min-h-0 flex-col border-b border-gray-200 lg:w-[22rem] lg:flex-none lg:border-b-0 lg:border-r">
              {/* Combined Datamandje and Gesprekken */}
              <div className="flex h-full min-h-0 flex-col p-4">
                <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
                  {/* Terug naar start */}
                  <div className="mb-3 flex-shrink-0">
                    <button
                      onClick={() => {
                        setShowInitialView(true);
                        setActiveConversationId(null);
                        setConversations((prev) => prev.map((c) => ({ ...c, active: false })));
                      }}
                      className="flex items-center gap-2 text-xs text-gray-600 hover:text-gray-900 transition-colors"
                    >
                      <RiArrowLeftSLine className="w-4 h-4" />
                      Terug naar start
                    </button>
                  </div>

                  {/* Datamandje */}
                  <div className="mb-4 flex min-h-0 flex-1 flex-col">
                    <div className="mb-2 flex flex-shrink-0 items-center justify-between">
                      <div className="flex items-center gap-1.5">
                        <h3 className="text-sm font-semibold text-gray-900">Datamandje</h3>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <Badge color="blue" className="text-xs px-1.5 py-0.5">{activeDataSources.length}</Badge>
                        <button
                          onClick={() => setIsWizardOpen(true)}
                          className="text-blue-600 hover:text-blue-700"
                          title="Voeg data toe"
                        >
                          <RiAddLine className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>

                    {activeConversation?.restoring ? (
                      <div className="flex items-center gap-1.5 text-xs text-gray-500">
                        <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-gray-400"></div>
                        Sessie herstellen...
                      </div>
                    ) : activeDataSources.length === 0 ? (
                      <p className="text-xs text-gray-500">Geen data in mandje</p>
                    ) : (
                      <div className="min-h-0 flex-1 space-y-1.5 overflow-y-auto pr-1">
                        {activeDataSources.map((source, index) => {
                          const endpointName = getEndpointName(source.endpoint_name);
                          const baseName = endpointName;
                          const filterText = source.parameters && Object.keys(source.parameters).length > 0
                            ? Object.entries(source.parameters)
                                .map(([k, v]) => `${k} = ${v}`)
                                .join(", ")
                            : null;
                          const pending = isPending(source);
                          const failed = hasFailed(source);
                          return (
                            <div
                              key={index}
                              className="corner-squircle relative rounded-[1.05rem] border border-gray-200 bg-gray-50 px-2.5 py-2"
                            >
                              <button
                                onClick={() =>
                                  pending
                                    ? void handleCancelDataSource(source)
                                    : setDeleteConfirm({ type: "datasource", source })
                                }
                                className="absolute top-1.5 right-1.5 text-gray-400 transition-colors hover:text-gray-600"
                                title={pending ? "Ophalen annuleren" : "Verwijderen"}
                              >
                                <RiCloseLine className="w-3 h-3" />
                              </button>
                              <div className="flex items-start gap-2">
                                <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full border-2 border-[rgba(96,147,172,0.3)]">
                                  {pending ? (
                                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-blue-200 border-t-blue-500" />
                                  ) : (
                                    getEndpointIcon(source.endpoint_name)
                                  )}
                                </div>
                                <div className="min-w-0 flex-1">
                                  <div className="mb-0.5 text-xs font-semibold text-gray-900">
                                    {baseName}
                                  </div>
                                  {filterText && (
                                    <div className="mb-0.5 text-[10px] text-gray-600">{filterText}</div>
                                  )}
                                  {pending ? (
                                    <span className="text-[10px] text-gray-500">
                                      {source.state === "QUEUED" ? "In de wachtrij..." : "Ophalen..."}
                                    </span>
                                  ) : failed ? (
                                    <span className="text-[10px] text-red-600">
                                      {source.state === "CANCELLED"
                                        ? "Geannuleerd"
                                        : source.error_message ?? "Ophalen mislukt"}
                                    </span>
                                  ) : (
                                  <div className="flex items-center gap-2">
                                    <span className="text-[10px] text-gray-500">
                                      ~{(source.row_count ?? 0).toLocaleString()} rijen
                                    </span>
                                    <button
                                      onClick={() => void handleViewDataSource(source, source.source_name || baseName)}
                                      className="flex items-center gap-0.5 text-[10px] text-blue-600 hover:text-blue-700"
                                    >
                                      <RiExternalLinkLine className="w-2.5 h-2.5" />
                                      Bekijk
                                    </button>
                                    <button
                                      onClick={() => void handleDownloadLibraryItem(source)}
                                      className="text-[10px] text-blue-600 hover:text-blue-700"
                                      title="Download"
                                    >
                                      <RiDownloadLine className="w-3 h-3" />
                                    </button>
                                    {source.task_id && !source.library_item_id && (
                                      <button
                                        onClick={() => void handleSaveToLibrary(source)}
                                        className="flex items-center gap-0.5 text-[10px] text-blue-600 hover:text-blue-700"
                                        title="Bewaar in databibliotheek"
                                      >
                                        <RiInboxLine className="w-2.5 h-2.5" />
                                        Bewaar
                                      </button>
                                    )}
                                  </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* Gegenereerde data */}
                  <div className="mb-4 flex min-h-0 flex-1 flex-col border-t border-gray-200 pt-4">
                    <div className="mb-2 flex flex-shrink-0 items-center justify-between">
                      <div className="flex items-center gap-1.5">
                        <h3 className="text-sm font-semibold text-gray-900">Gegenereerde data</h3>
                      </div>
                      <Badge color="green" className="text-xs px-1.5 py-0.5">{Object.keys(activeGeneratedData).length}</Badge>
                    </div>
                    {Object.keys(activeGeneratedData).length === 0 ? (
                      <p className="text-xs text-gray-500">Geen gegenereerde data</p>
                    ) : (
                      <div className="min-h-0 flex-1 space-y-1.5 overflow-y-auto pr-1">
                        {Object.entries(activeGeneratedData).map(([key, data]) => {
                          const truncatedKey = key.length > 25 ? key.substring(0, 25) + "..." : key;
                          return (
                            <div
                              key={key}
                              className="corner-squircle relative rounded-[1.05rem] border border-gray-200 bg-gray-50 px-2.5 py-2"
                            >
                              <button
                                onClick={() => setDeleteConfirm({ type: "generated", key })}
                                className="absolute top-1.5 right-1.5 text-gray-400 transition-colors hover:text-gray-600"
                                title="Verwijderen"
                              >
                                <RiCloseLine className="w-3 h-3" />
                              </button>
                              <div className="flex items-start gap-2">
                                <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full border-2 border-green-300 bg-green-50">
                                  <FileTextIcon className="w-4 h-4 text-green-600" />
                                </div>
                                <div className="min-w-0 flex-1">
                                  <div className="mb-0.5 text-xs font-semibold text-gray-900" title={key}>
                                    {truncatedKey}
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <span className="text-[10px] text-gray-500">
                                      {data.length.toLocaleString()} voorbeeldrijen
                                    </span>
                                    <button
                                      onClick={() => setViewDataModal({ data, title: key })}
                                      className="flex items-center gap-0.5 text-[10px] text-blue-600 hover:text-blue-700"
                                    >
                                      <RiExternalLinkLine className="w-2.5 h-2.5" />
                                      Bekijk
                                    </button>
                                    <button
                                      onClick={() => void handleDownloadGeneratedData(key)}
                                      className="text-[10px] text-blue-600 hover:text-blue-700"
                                      title="Download"
                                    >
                                      <RiDownloadLine className="w-3 h-3" />
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* Gesprekken */}
                  <div className="flex min-h-0 flex-1 flex-col border-t border-gray-200 pt-4">
                    <div className="mb-2 flex flex-shrink-0 items-center justify-between">
                      <div className="flex items-center gap-1.5">
                        <h3 className="text-sm font-semibold text-gray-900">Gesprekken</h3>
                      </div>
                      <Button
                        onClick={handleNewConversation}
                        color="primary"
                        className="text-[10px] px-2 py-1 h-auto"
                      >
                        <RiAddLine className="w-3 h-3 mr-0.5" />
                        Nieuw
                      </Button>
                    </div>

                    <div className="min-h-0 flex-1 space-y-1.5 overflow-y-auto pr-1">
                      {conversations.map((conv) => (
                        <div
                          key={conv.id}
                          className={`corner-squircle group relative w-full text-left px-2.5 py-1.5 rounded-[0.95rem] text-xs transition-colors ${
                            conv.active
                              ? "bg-blue-50 text-blue-700 border border-blue-200"
                              : "text-gray-700 hover:bg-gray-50 border border-transparent"
                          }`}
                        >
                          <button
                            onClick={() => handleSelectConversation(conv.id)}
                            className="w-full text-left pr-5"
                          >
                            <div className="font-medium">{conv.name}</div>
                            <div className="text-[10px] text-gray-500 mt-0.5">
                              {conv.dataSources.length} dataset
                            </div>
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleDeleteConversation(conv.id); }}
                            className="absolute top-1.5 right-1.5 opacity-0 group-hover:opacity-100 text-gray-400 hover:text-red-500 transition-all"
                            title="Verwijderen"
                          >
                            <RiCloseLine className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Main Content - Chat */}
            <div className="flex min-h-0 flex-1 flex-col">
              <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
                {activeDataSources.length > 0 ? (
                  <DataChat
                    key={activeConversationId}
                    dataSources={readyDataSources}
                    awaitingData={readyDataSources.length === 0 && activeDataSources.some(isPending)}
                    administrationCode={administrationCode}
                    onGeneratedDataChange={(generatedData) => {
                      if (!activeConversationId) return;
                      setConversations((prev) =>
                        prev.map((conv) => {
                          if (conv.id === activeConversationId) {
                            return { ...conv, generatedData };
                          }
                          return conv;
                        })
                      );
                    }}
                    initialMessages={activeConversation?.messages}
                    initialSessionId={activeConversation?.sessionId}
                    onMessagesChange={(messages, sessionId) => {
                      if (!activeConversationId) return;
                      setConversations((prev) =>
                        prev.map((conv) => {
                          if (conv.id === activeConversationId) {
                            return { ...conv, messages, sessionId };
                          }
                          return conv;
                        })
                      );
                    }}
                  />
                ) : (
                  <div className="flex flex-1 flex-col items-center justify-center p-8 text-center">
                    <div className="mb-8 flex h-24 w-24 items-center justify-center rounded-full bg-blue-100">
                      <SparklesIcon className="h-12 w-12 text-white" />
                    </div>
                    <p className="mb-4 text-gray-500">Stel me een vraag over je data — ik zoek het uit.</p>
                    <Button
                      onClick={() => setIsWizardOpen(true)}
                      color="primary"
                    >
                      <RiAddLine className="w-4 h-4 mr-1" />
                      Voeg data toe
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </Layout>

      {/* Data View Modal */}
      {viewDataModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => setViewDataModal(null)}
        >
          <div
            className="corner-squircle bg-white rounded-[1.75rem] shadow-xl w-full max-w-6xl h-[85vh] max-h-[85vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-6 border-b flex-shrink-0">
              <h3 className="text-lg font-semibold">{viewDataModal.title}</h3>
              <button
                onClick={() => setViewDataModal(null)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <RiCloseLine className="w-6 h-6" />
              </button>
            </div>
            <div className="flex-1 overflow-auto p-6">
              {viewDataModal.data.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm border-collapse">
                    <thead>
                      <tr className="border-b">
                        {Object.keys(viewDataModal.data[0]).map((key) => (
                          <th key={key} className="text-left p-2 font-semibold text-gray-700">
                            {key}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {viewDataModal.data.slice(0, 1000).map((row, idx) => (
                        <tr key={idx} className="border-b hover:bg-gray-50">
                          {Object.keys(viewDataModal.data[0]).map((key) => (
                            <td key={key} className="p-2 align-top text-gray-600">
                              {renderPreviewCell(row[key])}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {(viewDataModal.totalRows ?? viewDataModal.data.length) > viewDataModal.data.length && (
                    <div className="mt-4 text-sm text-gray-500 text-center">
                      Toont eerste {viewDataModal.data.length.toLocaleString()} van {(viewDataModal.totalRows ?? viewDataModal.data.length).toLocaleString()} rijen
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center text-gray-500 py-8">
                  Geen data beschikbaar
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <DataLoadingWizard
        isOpen={isWizardOpen}
        onClose={() => { setIsWizardOpen(false); setPreselectedEndpoint(undefined); }}
        onDataLoaded={handleDataLoaded}
        initialEndpoint={preselectedEndpoint}
        libraryItems={libraryItems}
        onLibraryItemDeleted={(id) => setLibraryItems((prev) => prev.filter((b) => b.id !== id))}
        administrationCode={administrationCode ?? undefined}
      />

      {/* Delete confirmation modal */}
      {deleteConfirm && (
        <div
          className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50"
          onClick={() => setDeleteConfirm(null)}
        >
          <div
            className="corner-squircle bg-white rounded-[1.5rem] shadow-xl w-full max-w-sm p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-sm font-semibold text-gray-900 mb-1">Weet je het zeker?</h3>
            <p className="text-xs text-gray-500 mb-5">
              {deleteConfirm.type === "generated"
                ? "Je kunt de data altijd opnieuw genereren door het de AI te vragen."
                : "Je moet de data opnieuw laden. Dit kan lang duren bij grote datasets."}
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="corner-squircle px-3 py-1.5 text-xs text-gray-600 hover:text-gray-900 border border-gray-200 rounded-md hover:bg-gray-50 transition-colors"
              >
                Annuleren
              </button>
              <button
                onClick={async () => {
                  if (deleteConfirm.type === "generated") {
                    const { key } = deleteConfirm;
                    const sessionId = activeConversation?.sessionId;
                    if (sessionId) await deleteGeneratedData(sessionId, key);
                    setConversations((prev) =>
                      prev.map((conv) => {
                        if (conv.id !== activeConversationId) return conv;
                        const next = { ...conv.generatedData };
                        delete next[key];
                        return { ...conv, generatedData: next };
                      })
                    );
                    toast.success("Data verwijderd");
                  } else {
                    const { source } = deleteConfirm;
                    if (!activeConversationId) return;
                    const sessionId = activeConversation?.sessionId;
                    if (sessionId) {
                      const sourceName = source.source_name || source.endpoint_name;
                      await deleteDataSource(sessionId, sourceName);
                    }
                    setConversations((prev) =>
                      prev.map((conv) => {
                        if (conv.id === activeConversationId) {
                          return { ...conv, dataSources: conv.dataSources.filter((s) => s !== source) };
                        }
                        return conv;
                      })
                    );
                    toast.success("Data verwijderd uit mandje");
                  }
                  setDeleteConfirm(null);
                }}
                className="corner-squircle px-3 py-1.5 text-xs text-white bg-red-500 hover:bg-red-600 rounded-md transition-colors"
              >
                Verwijderen
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default DataStudio;
